import React, { useState, useCallback } from "react";
import { useChartInfoData } from "../../chartHelper";
import { EllipsisText } from "../components/EllipsisText";

/* ── Hook ────────────────────────────────────────────────────── */

interface IUseTileInfoHoverReturn {
	isInfoVisible: boolean;
	show: () => void;
	hide: () => void;
}

export function useTileInfoHover(): IUseTileInfoHoverReturn {
	const [isInfoVisible, setIsInfoVisible] = useState(false);
	const show = useCallback(() => setIsInfoVisible(true), []);
	const hide = useCallback(() => setIsInfoVisible(false), []);
	return { isInfoVisible, show, hide };
}

/* ── InfoPanel ──────────────────────────────────────────────── */

interface InfoPanelProps {
	chartData: any;
}

const InfoPanel: React.FC<InfoPanelProps> = ({ chartData }) => {
	const { title, subtitle, quesNo, questionText, metadataItems } = useChartInfoData(chartData);
	const fallbackTitle = quesNo && questionText ? `${quesNo}. ${questionText}` : questionText || "Chart details";
	const displayTitle = title?.trim() ? title : fallbackTitle;

	return (
		<div className="tile-info-panel">
			<div className="tile-info-panel__header">
				<EllipsisText text={displayTitle} maxLines={2} className="font-semibold text-[--primary-color-2] leading-none tracking-[0.35px]" fontSize={12.5} />
				{subtitle && <EllipsisText text={subtitle} maxLines={2} className="text-[#63646B] leading-none tracking-normal" fontSize={10} />}
			</div>
			<div className="tile-info-panel__content">
				{(quesNo || questionText) && (
					<div className="tile-info-panel__section">
						<p className="tile-info-panel__section-text text-[#63646B] italic text-base-1 leading-[20px] tracking-normal">
							{quesNo && <span>{quesNo}. </span>}
							{questionText}
						</p>
					</div>
				)}
				{metadataItems.length > 0 && (
					<div className="tile-info-panel__section">
						<p className="tile-info-panel__section-text tile-info-panel__metadata-line text-[#B70000] text-base-1 leading-[20px] tracking-normal">
							{metadataItems.map((node, idx) => (
								<React.Fragment key={idx}>
									{idx > 0 && <span className="tile-info-panel__meta-sep text-[#B70000] text-base-1 leading-[20px] tracking-normal">{" | "}</span>}
									{node}
								</React.Fragment>
							))}
						</p>
					</div>
				)}
			</div>
		</div>
	);
};

/* ── TileInfoWrapper — hover-based ──────────────────────────── */

interface TileInfoWrapperProps {
	children: React.ReactNode;
	isInfoVisible: boolean;
	onShow: () => void;
	onHide: () => void;
	chartData: any;
	showInfoIcon?: boolean;
}

// AFTER
/**
 * TileInfoWrapper — wraps a chart tile with an overlay toggle between
 * chart view and info panel. Uses click-based dot navigation (carousel-style)
 * at the bottom of the tile instead of a hover-based info icon.
 */
export const TileInfoWrapper: React.FC<TileInfoWrapperProps> = ({ children, isInfoVisible, onShow, onHide, chartData, showInfoIcon = true }) => {
	return (
		<div className="tile-info-wrapper">
			<div className="tile-info-wrapper__content">
				{/* Chart layer */}
				<div
					className="tile-info-wrapper__layer"
					style={{
						opacity: isInfoVisible ? 0 : 1,
						pointerEvents: isInfoVisible ? "none" : "auto",
						visibility: isInfoVisible ? "hidden" : "visible",
					}}>
					{children}
				</div>

				{/* Info panel layer */}
				{showInfoIcon && (
					<div
						className="tile-info-wrapper__layer tile-info-wrapper__layer--info"
						style={{
							opacity: isInfoVisible ? 1 : 0,
							pointerEvents: isInfoVisible ? "auto" : "none",
							visibility: isInfoVisible ? "visible" : "hidden",
						}}>
						<InfoPanel chartData={chartData} />
					</div>
				)}
			</div>

			{/* Dot navigation — click-based toggle between chart and info views */}
			{showInfoIcon && (
				<div className="tile-info-dots">
					<button className={`tile-info-dot${!isInfoVisible ? " tile-info-dot--active" : ""}`} onClick={onHide} aria-label="Show chart" type="button" />
					<button className={`tile-info-dot${isInfoVisible ? " tile-info-dot--active" : ""}`} onClick={onShow} aria-label="Show info" type="button" />
				</div>
			)}
		</div>
	);
};
